import { forwardRef, useId } from 'react';
import type {
  InputHTMLAttributes,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/cn';

/* ============================================================================================
   Field wrapper

   Owns the label/description/error relationship so every control is announced correctly. Doing
   this per-control is how `aria-describedby` ends up missing on half the forms in an application.
   ============================================================================================ */

interface FieldProps {
  label: string;
  htmlFor: string;
  error?: string;
  description?: string;
  required?: boolean;
  children: ReactNode;
  className?: string;
}

export function Field({
  label,
  htmlFor,
  error,
  description,
  required,
  children,
  className,
}: FieldProps) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={htmlFor} className="block text-small font-medium text-text-primary">
        {label}
        {required && (
          <span aria-hidden className="ml-0.5 text-danger">
            *
          </span>
        )}
        {required && <span className="sr-only"> (required)</span>}
      </label>
      {description && (
        <p id={`${htmlFor}-description`} className="text-caption text-text-muted">
          {description}
        </p>
      )}
      {children}
      {error && (
        // Announced when it appears, so a screen-reader user learns of the problem without
        // hunting for it.
        <p id={`${htmlFor}-error`} role="alert" className="text-caption text-danger-text">
          {error}
        </p>
      )}
    </div>
  );
}

const CONTROL = cn(
  'w-full rounded-md border border-border-default bg-surface px-3 text-body text-text-primary',
  'placeholder:text-text-muted',
  'transition-[border-color,box-shadow] duration-150',
  'hover:border-border-strong',
  'focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/25',
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-text-muted',
  'aria-[invalid=true]:border-danger aria-[invalid=true]:ring-danger/25',
);

/* ============================================================================================
   Controls
   ============================================================================================ */

interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  description?: string;
  leadingIcon?: ReactNode;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, error, description, leadingIcon, className, id, required, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const control = (
    <div className="relative">
      {leadingIcon && (
        <span
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-text-muted"
        >
          {leadingIcon}
        </span>
      )}
      <input
        ref={ref}
        id={inputId}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={
          cn(description && `${inputId}-description`, error && `${inputId}-error`) || undefined
        }
        className={cn(CONTROL, 'h-9', leadingIcon && 'pl-9', className)}
        {...props}
      />
    </div>
  );

  if (!label) return control;
  return (
    <Field
      label={label}
      htmlFor={inputId}
      error={error}
      description={description}
      required={required}
    >
      {control}
    </Field>
  );
});

interface TextAreaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  description?: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(
  { label, error, description, className, id, required, rows = 3, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const control = (
    <textarea
      ref={ref}
      id={inputId}
      rows={rows}
      required={required}
      aria-invalid={error ? true : undefined}
      aria-describedby={
        cn(description && `${inputId}-description`, error && `${inputId}-error`) || undefined
      }
      className={cn(CONTROL, 'resize-y py-2 leading-6', className)}
      {...props}
    />
  );

  if (!label) return control;
  return (
    <Field
      label={label}
      htmlFor={inputId}
      error={error}
      description={description}
      required={required}
    >
      {control}
    </Field>
  );
});

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  description?: string;
  /** Rendered as a disabled first option, so the control communicates "nothing chosen yet". */
  placeholder?: string;
  options: { value: string; label: string; disabled?: boolean }[];
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, error, description, placeholder, options, className, id, required, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const control = (
    <select
      ref={ref}
      id={inputId}
      required={required}
      aria-invalid={error ? true : undefined}
      aria-describedby={
        cn(description && `${inputId}-description`, error && `${inputId}-error`) || undefined
      }
      className={cn(CONTROL, 'h-9 appearance-none bg-no-repeat pr-8', className)}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        backgroundPosition: 'right 0.625rem center',
      }}
      {...props}
    >
      {placeholder && (
        <option value="" disabled={required}>
          {placeholder}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );

  if (!label) return control;
  return (
    <Field
      label={label}
      htmlFor={inputId}
      error={error}
      description={description}
      required={required}
    >
      {control}
    </Field>
  );
});

/** A search box. Debouncing belongs to the caller, which knows what it is querying. */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search…',
  className,
  'aria-label': ariaLabel = 'Search',
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  'aria-label'?: string;
}) {
  return (
    <TextInput
      type="search"
      value={value}
      aria-label={ariaLabel}
      placeholder={placeholder}
      leadingIcon={<Search className="size-4" />}
      className={cn('sm:w-72', className)}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

export function Checkbox({
  label,
  description,
  id,
  className,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode; description?: string }) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  return (
    <div className={cn('flex gap-2.5', className)}>
      <input
        id={inputId}
        type="checkbox"
        className="mt-0.5 size-4 shrink-0 rounded border-border-strong text-brand accent-[var(--brand)]"
        aria-describedby={description ? `${inputId}-description` : undefined}
        {...props}
      />
      <div className="min-w-0">
        <label htmlFor={inputId} className="block text-small text-text-primary">
          {label}
        </label>
        {description && (
          <p id={`${inputId}-description`} className="text-caption text-text-muted">
            {description}
          </p>
        )}
      </div>
    </div>
  );
}
